import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, InputLabel } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useField, useFormikContext } from 'formik';
import BaseTextField from '@material-ui/core/TextField';

import { BUTTON_COLOR, BUTTON_VARIANT, DIALOG_SIZE, TEXT_FIELD_COLOR } from '../../enum/formProps';
import { MultimediaValues } from '../../../../features/multimedia/model/multimediaModel';
import Dialog from '../../../Dialog';
import UploadFileForm from './UploadFileForm';
import * as Selectors from '../../../../features/multimedia/redux/multimediaSelectors';
import * as Actions from '../../../../features/multimedia/redux/multimediaActions';

import './Image.scss';
import Panel from '../../../Tabs/Panel';
import Tabs from '../../../Tabs';
import Tab from '../../../Tabs/Tab';
import Container from '../../../Container';
import SelectFileForm from './SelectFileForm';

interface ImageProps {
  dialogTitle: string;
  dialogButtonActionLabel: string;
  uploadSuccessMessage: string;
  label: string;
  name: string;
  color?: TEXT_FIELD_COLOR;
  disabled?: boolean;
  iri?: string | null;
}

const Image: React.FC<ImageProps> = ({
  label,
  dialogTitle,
  dialogButtonActionLabel,
  uploadSuccessMessage,
  ...props
}) => {
  const multimedia = useSelector(Selectors.multimedia);
  const dispatch = useDispatch();
  const { setFieldValue } = useFormikContext<MultimediaValues>();
  const { t } = useTranslation();
  const [field, meta] = useField(props);
  const isError = !!(meta.error && meta.touched);
  const [open, setOpen] = useState(false);
  const [iri, setIri] = useState(props.iri);
  const [activeMenuItem, setActiveMenuItem] = useState(1);
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveMenuItem(newValue);
  };

  useEffect(() => {
    props.iri && setIri(props.iri);
  }, [props.iri]);

  const handleClickOpen = useCallback(() => {
    !props.disabled && setOpen(true);
  }, [open, props.disabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [open]);

  const handleUploadSuccess = useCallback(() => {
    setOpen(false);
    setFieldValue(props.name, multimedia.iri);
  }, [open, props.name, multimedia]);

  const handleSelectSuccess = useCallback(
    (iri: string) => {
      setOpen(false);
      setFieldValue(props.name, iri);
    },
    [open, props.name, multimedia],
  );

  const handleClickDelete = useCallback(() => {
    dispatch(Actions.resetMultimedia());
    setFieldValue(props.name, '');
    setIri(null);
  }, [props.name]);

  return (
    <Grid container spacing={0} className="Image-Container">
      <Grid item xs={4} md={3} lg={2} xl={1}>
        <InputLabel className="Image-InputLabel" error={isError} disabled={props.disabled}>
          {t(label)}
        </InputLabel>
      </Grid>
      <Grid item xs={8} md={9} lg={10} xl={11}>
        <Button
          className="Image-ButtonAdd"
          variant={BUTTON_VARIANT.CONTAINED}
          color={BUTTON_COLOR.DEFAULT}
          component="span"
          onClick={handleClickOpen}
          disabled={props.disabled}
        >
          {t('button.add')}
        </Button>
        <Button
          className="Image-ButtonDelete"
          variant={BUTTON_VARIANT.CONTAINED}
          color={BUTTON_COLOR.DEFAULT}
          component="span"
          onClick={handleClickDelete}
          disabled={props.disabled || (!multimedia.iri.length && !iri?.length)}
        >
          {t('button.delete')}
        </Button>
        <BaseTextField
          id={props.name}
          {...field}
          {...props}
          disabled={true}
          className="Image-BaseTextField"
          error={isError}
          fullWidth
          helperText={t(meta.error ?? '')}
          color={props.color}
          onClick={handleClickOpen}
        />
      </Grid>
      <Dialog
        open={open}
        hideActions={true}
        disableBackdropClick={true}
        title={dialogTitle}
        dialogSize={DIALOG_SIZE.MD}
        fullWidth={true}
        buttonActionLabel={dialogButtonActionLabel}
        isTabSet={true}
      >
        <Tabs value={activeMenuItem} onChange={handleChange}>
          <Tab label={t('multimedia.add_new')} value={1} />
          <Tab label={t('multimedia.select_available')} value={2} />
        </Tabs>
        <Panel index={1} value={activeMenuItem}>
          <Container className="Image-Panel">
            <UploadFileForm
              onSuccess={handleUploadSuccess}
              onClose={handleClose}
              successMessage={uploadSuccessMessage}
              buttonActionLabel={dialogButtonActionLabel}
            />
          </Container>
        </Panel>
        <Panel index={2} value={activeMenuItem}>
          <Container className="Image-Panel">
            <SelectFileForm
              onSuccess={handleSelectSuccess}
              onClose={handleClose}
              buttonActionLabel={t('button.select')}
            />
          </Container>
        </Panel>
      </Dialog>
    </Grid>
  );
};

export default Image;
